import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Hint, Inscription, RangeInput, Text, useResponsive } from '@common-fe/common-fe';
import styled from 'styled-components';

import { RiskControlOptions } from '@/common/constants';
import { RiskOption } from '@/common/types';
import colors from '@/styles/colors';

import { RISKS_MODELS } from '../investments.types';

interface Props {
  riskValue: string;
  recommendedRisk?: string;
  currentRisk?: string;
  disabled?: boolean;
  labelColor?: string;
  labelText?: string;
  currentLabelColor?: string;
  currentLabelText?: string;
  isHintHidden?: boolean;
  riskOptions: RiskOption[];
  setRiskValue: (value: string) => void;
  setRiskModal: (value: boolean) => void;
}

const RecommendedTitleWrapper = styled(Box)`
  position: absolute;
  top: 0px;
  cursor: pointer;
`;

const RiskCard: React.FC<Props> = ({
  riskValue,
  recommendedRisk,
  currentRisk,
  disabled,
  labelColor,
  labelText,
  currentLabelColor,
  currentLabelText,
  isHintHidden,
  riskOptions,
  setRiskValue,
  setRiskModal,
}) => {
  const { isMobile } = useResponsive();
  const { t } = useTranslation();

  const details = useMemo(() => {
    const currentItem = RISKS_MODELS[riskValue as RiskControlOptions];
    return {
      description: t(currentItem?.description || ''),
      label: t(currentItem?.label || ''),
    };
  }, [riskValue, t]);

  const titleLabel = useMemo(
    () => (
      <Box
        round={{ corner: 'bottom', size: '4px' }}
        pad={{ vertical: 'xxxs', horizontal: 'xs' }}
        background={labelColor || colors.success}
      >
        <Text size="small" color="textOnColor">
          {labelText}
        </Text>
      </Box>
    ),
    [labelColor, labelText]
  );
  const currentTitleLabel = useMemo(
    () => (
      <Box
        round={{ corner: 'bottom', size: '4px' }}
        pad={{ vertical: 'xxxs', horizontal: 'xs' }}
        background={currentLabelColor || colors.stressContainer}
      >
        <Text size="small" color="textOnColor">
          {currentLabelText}
        </Text>
      </Box>
    ),
    [currentLabelColor, currentLabelText]
  );

  return (
    <Box
      background="canvas"
      round="container1Round"
      width={isMobile ? 'auto' : '580px'}
      height={isMobile ? 'auto' : '250px'}
      pad={
        isMobile
          ? {
              top: disabled ? 'spacing32' : 'spacing24',
              bottom: 'spacing16',
              horizontal: 'spacing16',
            }
          : {
              vertical: disabled ? 'spacing48' : 'spacing32',
              horizontal: 'spacing24',
            }
      }
      data-testid="riskcard-wrapper-id"
      align="center"
      border={{ size: 'small', color: 'border2' }}
      style={{
        position: 'relative',
      }}
    >
      <RecommendedTitleWrapper direction="row" gap="spacing8">
        {currentTitleLabel && currentRisk === riskValue ? currentTitleLabel : <></>}
        {isHintHidden ? (
          titleLabel
        ) : recommendedRisk === riskValue ? (
          <Hint testId="risk-card_hint" hintElement={titleLabel} hintHeight={500}>
            <Text textAlign="center" data-testId="risk-card_hint_text">
              {t(
                'This option best matches your investment portfolio (recommended based on your Portfolio Questionnaire).'
              )}
            </Text>
          </Hint>
        ) : (
          <></>
        )}
      </RecommendedTitleWrapper>
      <RangeInput
        testId="risk-card_slide"
        options={riskOptions}
        value={riskValue}
        onChange={setRiskValue}
        disabled={disabled}
        colorsThumb={colors.buttonPrimary}
        width={540}
        recommendedOption={recommendedRisk}
        currentOption={currentRisk}
      />
      <Text
        textAlign="center"
        weight="bold"
        size="medium"
        data-testId="risk-card_slide_risk_header"
        margin={isMobile ? {} : { bottom: 's', top: isHintHidden ? 'xxxl' : 's' }}
      >
        {details.label}
      </Text>

      <Text textAlign="center" data-testId="risk-card_slide_risk_description">
        {details.description}
        <Inscription
          onClick={() => setRiskModal(true)}
          margin={{ left: 'spacing4' }}
          cursor="pointer"
          weight={500}
          color="textAccent"
          data-testid="risk-card_read-more"
        >
          {t('Read more')}
        </Inscription>
      </Text>
    </Box>
  );
};

export default RiskCard;
